import { Link } from 'react-router-dom';
import { FileText, Building2, Shield, Crown, AlertTriangle, CreditCard, Truck, UserX, Scale, GraduationCap } from 'lucide-react';
import SEO from '../components/shared/SEO';

const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="terms-of-service-page">
      <SEO 
        title="Terms of Service | Queen B Logistics"
        description="Terms of Service for Queen B Holdings LLC, DBA Queen B Logistics - Review our service terms and conditions."
      />
      
      {/* Header */}
      <section className="py-16 bg-[#050505] border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-8 h-8 text-[#D4AF37]" />
            <span className="text-[#D4AF37] text-sm font-bold uppercase tracking-widest">Legal</span>
          </div>
          <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white uppercase mb-4">
            Terms of Service
          </h1>
          <p className="text-white/60">
            Queen B Holdings LLC, DBA Queen B Logistics<br />
            <span className="text-white/40">Last Updated: February 2026</span>
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="prose prose-invert max-w-none">
            
            <PolicySection number="1" title="Acceptance of Terms">
              <p>By using our website, Hive portal, or purchasing services, you agree to these Terms.</p>
              <p className="text-amber-400 font-bold">If you do not agree, do not use our services.</p>
            </PolicySection>

            <PolicySection number="2" title="Business Model Overview" icon={Building2}>
              <p>Queen B Logistics operates under a structured lifecycle model:</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6">
                <div className="bg-[#CD7F32]/10 border border-[#CD7F32]/30 p-4 text-center">
                  <Building2 className="w-8 h-8 text-[#CD7F32] mx-auto mb-2" />
                  <p className="text-[#CD7F32] font-bold m-0">BUILD</p>
                  <p className="text-white/50 text-sm m-0">Authority Formation (Signature Program)</p>
                </div>
                <div className="bg-[#C0C0C0]/10 border border-[#C0C0C0]/30 p-4 text-center">
                  <Shield className="w-8 h-8 text-[#C0C0C0] mx-auto mb-2" />
                  <p className="text-[#C0C0C0] font-bold m-0">PROTECT</p>
                  <p className="text-white/50 text-sm m-0">Compliance Monitoring</p>
                </div>
                <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-4 text-center">
                  <Crown className="w-8 h-8 text-[#D4AF37] mx-auto mb-2" />
                  <p className="text-[#D4AF37] font-bold m-0">GROW</p>
                  <p className="text-white/50 text-sm m-0">Dispatch Services</p>
                </div>
              </div>
              <p>Services are modular and subscription-based after authority activation.</p>
            </PolicySection>

            <PolicySection number="3" title="Signature Program (Build Services)">
              <p>Signature Program services include structured guidance through:</p>
              <ul>
                <li>Intake</li>
                <li>Formation</li>
                <li>Authority filing support</li>
                <li>Compliance setup</li>
                <li>Launch activation</li>
              </ul>
              <div className="bg-amber-500/10 border border-amber-500/30 p-4 my-4">
                <p className="text-amber-400 m-0"><strong>Signature Program fees are non-refundable once work has begun</strong>, except where explicitly stated.</p>
              </div>
              <p>Authority approval is determined by FMCSA and government agencies — <strong>we do not guarantee approval</strong>.</p>
            </PolicySection>

            <PolicySection number="4" title="Authority Activation & Grace Period">
              <p>When authority is marked active:</p>
              <ul>
                <li>Build phase closes</li>
                <li>A <strong className="text-[#D4AF37]">14-day grace period</strong> begins</li>
                <li>Client may select Compliance or Bundle plan</li>
              </ul>
              <div className="bg-green-500/10 border border-green-500/30 p-4 my-4">
                <p className="text-green-400 m-0"><strong>No subscription billing occurs until client selects and confirms a plan.</strong></p>
              </div>
              <p>If no plan is selected by the end of the grace period, system access may be restricted.</p>
            </PolicySection>

            <PolicySection number="5" title="Subscription Services" icon={CreditCard}>
              <p>Our services are billed on different cycles:</p>
              <ul>
                <li><strong className="text-[#D4AF37]">Dispatch Services</strong> — billed weekly</li>
                <li><strong className="text-[#C0C0C0]">Compliance Monitoring</strong> — billed monthly</li>
              </ul>
              <p className="mt-3">Additional billing terms:</p>
              <ul>
                <li>Plans must be manually selected</li>
                <li>Billing occurs only after confirmation</li>
                <li>Pricing tiers vary by service level</li>
                <li>Bundle plans receive <strong className="text-[#D4AF37]">20% discount</strong></li>
              </ul>
              <p>Subscriptions renew automatically unless canceled before the renewal date.</p>
            </PolicySection>

            <PolicySection number="6" title="Queen B Academy" icon={GraduationCap}>
              <p>Queen B Academy is a digital educational platform offering courses including:</p>
              <ul>
                <li>CDL Mastery 101</li>
                <li>Dispatcher 101</li>
                <li>Trucking 101</li>
                <li>Freight Broker 101</li>
              </ul>
              <h4 className="text-white font-bold mt-6 mb-3">Purchase Terms</h4>
              <ul>
                <li>Individual courses: $250 each</li>
                <li>Complete Bundle (all 4 courses): $800</li>
                <li>Access is granted via unique QBA-XXXXX access codes</li>
              </ul>
              <div className="bg-red-500/10 border border-red-500/30 p-4 my-4">
                <p className="text-red-400 m-0 font-bold">All Queen B Academy purchases are final and non-refundable.</p>
              </div>
              <h4 className="text-white font-bold mt-6 mb-3">Course Access</h4>
              <ul>
                <li>Access is immediate upon purchase and code redemption</li>
                <li>Course content, quizzes, and resources are for personal use only</li>
                <li>Access codes are single-use and non-transferable</li>
                <li>Sharing, reselling, or redistributing course materials is prohibited</li>
              </ul>
              <h4 className="text-white font-bold mt-6 mb-3">Intellectual Property</h4>
              <p>All Academy course content, including videos, documents, quizzes, and downloadable resources, is the intellectual property of Queen B Logistics. Unauthorized reproduction or distribution is strictly prohibited.</p>
            </PolicySection>

            <PolicySection number="7" title="Additional Truck Policy" icon={Truck}>
              <p>Each additional truck may require additional monitoring and/or dispatch fees.</p>
              <p>Pricing is disclosed before activation.</p>
            </PolicySection>

            <PolicySection number="8" title="No Revenue Guarantee" icon={AlertTriangle}>
              <p>We do not guarantee:</p>
              <ul>
                <li>Specific load volume</li>
                <li>Specific revenue amounts</li>
                <li>Broker approvals</li>
                <li>Market conditions</li>
              </ul>
              <p className="text-white/50">Dispatch is strategy-based but dependent on industry conditions.</p>
            </PolicySection>

            <PolicySection number="9" title="Client Responsibilities">
              <p>Clients must:</p>
              <ul>
                <li>Maintain active insurance</li>
                <li>Maintain authority compliance</li>
                <li>Communicate operational changes</li>
                <li>Provide accurate documentation</li>
              </ul>
              <p className="text-amber-400">Failure to comply may result in service suspension.</p>
            </PolicySection>

            <PolicySection number="10" title="Limitation of Liability" icon={Scale}>
              <p>Queen B Logistics is not liable for:</p>
              <ul>
                <li>Government filing delays</li>
                <li>FMCSA decisions</li>
                <li>Market downturns</li>
                <li>Broker non-payment</li>
                <li>Carrier operational negligence</li>
              </ul>
            </PolicySection>

            <PolicySection number="11" title="Termination" icon={UserX}>
              <p>We reserve the right to terminate service for:</p>
              <ul>
                <li>Non-payment</li>
                <li>Fraud</li>
                <li>Misrepresentation</li>
                <li>Regulatory violations</li>
              </ul>
            </PolicySection>

          </div>

          {/* Back Link */}
          <div className="mt-12 pt-8 border-t border-white/10">
            <Link to="/" className="text-[#D4AF37] hover:underline">
              ← Back to Home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

const PolicySection = ({ number, title, icon: Icon, children }) => (
  <div className="mb-12">
    <div className="flex items-center gap-3 mb-4">
      <span className="w-8 h-8 bg-[#D4AF37]/20 text-[#D4AF37] flex items-center justify-center font-bold text-sm">
        {number}
      </span>
      <h2 className="font-['Barlow_Condensed'] text-2xl font-bold text-white uppercase m-0">
        {title}
      </h2>
      {Icon && <Icon className="w-5 h-5 text-[#D4AF37]" />}
    </div>
    <div className="text-white/70 leading-relaxed pl-11">
      {children}
    </div>
  </div>
);

export default TermsOfServicePage;
